import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import "./scheduling.css";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import BookingModal from "../../components/scheduling/BookingModal";
import Toast from "../../components/scheduling/Toast";
import { SkeletonRow } from "../../components/scheduling/Skeleton";

function startOfWeek(d) {
  const x = new Date(d);
  const day = x.getDay(); // Sun=0
  const diff = (day + 6) % 7; // Mon start
  x.setDate(x.getDate() - diff);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function fmtDay(d) {
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export default function Calendar() {
  const { companyId } = useCompany();

  const [spaces, setSpaces] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [spaceId, setSpaceId] = useState("");

  const [toast, setToast] = useState({
    open: false,
    type: "info",
    message: "",
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDefaults, setModalDefaults] = useState({
    spaceId: "",
    start: null,
    end: null,
  });

  const days = useMemo(
    () => Array.from({ length: 7 }).map((_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  async function load() {
    if (!companyId) return;
    setBusy(true);
    setErr("");

    try {
      const { data: sData, error: sErr } = await supabase
        .from("spaces")
        .select("*")
        .eq("company_id", companyId)
        .order("name");

      if (sErr) throw sErr;
      setSpaces(sData || []);

      const start = new Date(weekStart);
      const end = addDays(weekStart, 7);
      const startIso = start.toISOString();
      const endIso = end.toISOString();

      let q = supabase
        .from("space_bookings")
        .select("*")
        .eq("company_id", companyId)
        .in("status", ["confirmed", "cancelled", "completed"])
        .gte("start_at", startIso)
        .lt("start_at", endIso)
        .order("start_at", { ascending: true });

      if (spaceId) q = q.eq("space_id", spaceId);

      const { data: bData, error: bErr } = await q;
      if (bErr) throw bErr;

      setBookings(bData || []);
    } catch (e) {
      setErr(e?.message || "Failed to load calendar");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, weekStart, spaceId]);

  useEffect(() => {
    if (!companyId) return;
    const channel = supabase
      .channel("sched_calendar")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "space_bookings",
          filter: `company_id=eq.${companyId}`,
        },
        () => load()
      )
      .subscribe();
    return () => supabase.removeChannel(channel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, weekStart, spaceId]);

  const spaceMap = useMemo(
    () => new Map(spaces.map((s) => [s.id, s])),
    [spaces]
  );

  const bookingsByDay = useMemo(() => {
    const m = new Map();
    days.forEach((d) => m.set(d.toDateString(), []));
    bookings.forEach((b) => {
      const key = new Date(b.start_at).toDateString();
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(b);
    });
    return m;
  }, [bookings, days]);

  const activeSpaces = useMemo(
    () => spaces.filter((s) => s.status === "active"),
    [spaces]
  );

  return (
    <div className="sched-card">
      <div className="sched-card-head">
        <div>
          <h3 className="sched-h3">Calendar (Week)</h3>
          <p className="sched-muted">
            Quick weekly view. Click “Book” on a day to create a booking.
          </p>
        </div>

        <div className="sched-row gap">
          <select value={spaceId} onChange={(e) => setSpaceId(e.target.value)}>
            <option value="">All spaces</option>
            {spaces.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} {s.status !== "active" ? "(Inactive)" : ""}
              </option>
            ))}
          </select>

          <button
            className="sched-btn ghost"
            onClick={() => setWeekStart((d) => addDays(d, -7))}
          >
            <ChevronLeft size={16} /> Prev
          </button>
          <button
            className="sched-btn ghost"
            onClick={() => setWeekStart(startOfWeek(new Date()))}
          >
            Today
          </button>
          <button
            className="sched-btn ghost"
            onClick={() => setWeekStart((d) => addDays(d, 7))}
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {busy ? (
        <>
          <SkeletonRow cols={4} />
          <SkeletonRow cols={4} />
        </>
      ) : err ? (
        <div className="sched-error">{err}</div>
      ) : (
        <div className="sched-week">
          {days.map((d) => {
            const items = bookingsByDay.get(d.toDateString()) || [];
            return (
              <div key={d.toISOString()} className="sched-day">
                <div className="sched-day-head">
                  <div className="sched-day-title">{fmtDay(d)}</div>
                  <button
                    className="sched-mini"
                    title="New booking"
                    onClick={() => {
                      if (activeSpaces.length === 0) {
                        setToast({
                          open: true,
                          type: "error",
                          message: "No active spaces available",
                        });
                        return;
                      }
                      const start = new Date(d);
                      start.setHours(9, 0, 0, 0);
                      const end = new Date(d);
                      end.setHours(10, 0, 0, 0);

                      setModalDefaults({
                        spaceId: activeSpaces[0]?.id || "",
                        start,
                        end,
                      });
                      setModalOpen(true);
                    }}
                  >
                    <Plus size={16} />
                  </button>
                </div>

                {items.length === 0 ? (
                  <div className="sched-day-empty">No bookings</div>
                ) : (
                  <div className="sched-day-items">
                    {items.map((b) => {
                      const sp = spaceMap.get(b.space_id);
                      const label = b.private_mode ? "Busy" : b.title || "—";
                      return (
                        <div
                          key={b.id}
                          className={`sched-booking-chip ${b.status}`}
                        >
                          <div className="sched-chip-top">
                            <span className="sched-chip-space">
                              {sp?.name || "—"}
                            </span>
                            <span className="sched-chip-status">
                              {b.status}
                            </span>
                          </div>
                          <div className="sched-chip-title">{label}</div>
                          <div className="sched-chip-time">
                            {new Date(b.start_at).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}{" "}
                            -{" "}
                            {new Date(b.end_at).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <BookingModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        mode="create"
        spaces={activeSpaces}
        defaultSpaceId={modalDefaults.spaceId}
        defaultStart={modalDefaults.start}
        defaultEnd={modalDefaults.end}
        onSaved={() => load()}
      />

      <Toast
        open={toast.open}
        type={toast.type}
        message={toast.message}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
      />
    </div>
  );
}
